import type { CityConfig } from "../hawaii-city/types";

export const pearlCityConfig: CityConfig = {
  city: "Pearl City",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "pearl-city",
  route: "/cities/pearl-city",
  title: "Emergency & Expedited Passport Services in Pearl City, HI — Same-Day Processing | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Pearl City, HI — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Pearl Harbor area and Central Oahu travelers.",
  h1: "Emergency & Expedited Passport Services in Pearl City, HI",
  heroSubtitle: "Passport expired? Flying soon? We help Pearl City and Central Oahu travelers get their passports fast. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20view%20of%20Pearl%20Harbor%20Hawaii%20with%20USS%20Arizona%20Memorial%20and%20calm%20blue%20waters%2C%20green%20hills%20of%20Central%20Oahu%20in%20background%2C%20sunny%20tropical%20day%20with%20clear%20skies%2C%20warm%20golden%20light%2C%20professional%20landscape%20photography%2C%20no%20readable%20text%20or%20signs%2C%20serene%20and%20peaceful%20atmosphere&width=1800&height=700&seq=pearl-city-hero-01&orientation=landscape",
  heroImageAlt: "Pearl Harbor and Central Oahu landscape with USS Arizona Memorial",
  geoPosition: "21.3970;-157.9792",
  geoPlacename: "Pearl City, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "About 10 miles from Pearl City",
  },
  nearbyCitiesZips: [
    { name: "Pearl City", zips: "96782" },
    { name: "Aiea", zips: "96701" },
    { name: "Waimalu", zips: "96701" },
    { name: "Halawa", zips: "96818" },
    { name: "Honolulu", zips: "96815" },
    { name: "Waipahu", zips: "96797" },
    { name: "Ewa Beach", zips: "96706" },
    { name: "Kapolei", zips: "96707" },
    { name: "Mililani", zips: "96789" },
    { name: "Wahiawa", zips: "96786" },
    { name: "Kailua", zips: "96734" },
    { name: "Kaneohe", zips: "96744" },
    { name: "Schofield Barracks", zips: "96857" },
    { name: "Waipio", zips: "96797" },
    { name: "Nanakuli", zips: "96792" },
    { name: "Waianae", zips: "96792" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping to send your documents directly to us — we handle the rest." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed." },
  ],
  testimonials: [
    {
      name: "James W.",
      location: "Pearl City, HI",
      rating: 5,
      text: "Military deployment came up faster than expected and my passport was expired. Fast Passport Center had my renewal processed in 4 days. They understood the urgency and made it happen.",
      service: "Passport Renewal",
    },
    {
      name: "Maria S.",
      location: "Aiea, HI",
      rating: 5,
      text: "Our family trip to the Philippines was in 3 weeks and my daughter's passport was missing. Fast Passport Center handled everything from Pearl City — shipped documents, reviewed them, and got her passport back in time.",
      service: "Child Passport",
    },
    {
      name: "Robert K.",
      location: "Waipahu, HI",
      rating: 5,
      text: "Business conference in Seoul with only a week's notice. Started my order online from Waipahu, shipped overnight, and had my expedited passport in 5 days. Unbelievable service from Hawaii.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "pearl-city-usps",
      name: "Pearl City Post Office",
      type: "USPS Passport Acceptance",
      address: "550 Kamehameha Hwy",
      city: "Pearl City",
      state: "HI",
      zip: "96782",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Pearl City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary post office for Pearl City area. Appointment required. Confirm photo services.",
      icon: "ri-mail-line",
    },
    {
      id: "pearl-city-aiea",
      name: "Aiea Post Office",
      type: "USPS Passport Acceptance",
      address: "99-065 Kauhale St",
      city: "Aiea",
      state: "HI",
      zip: "96701",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Aiea — adjacent to Pearl City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Nearby Aiea option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "pearl-city-waipahu",
      name: "Waipahu Post Office",
      type: "USPS Passport Acceptance",
      address: "94-245 Leokane St",
      city: "Waipahu",
      state: "HI",
      zip: "96797",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Waipahu — about 5 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Waipahu option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "pearl-city-ewa",
      name: "Ewa Beach Post Office",
      type: "USPS Passport Acceptance",
      address: "91-1001 Renton Rd",
      city: "Ewa Beach",
      state: "HI",
      zip: "96706",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Ewa Beach — about 8 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Ewa Beach area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "pearl-city-mililani",
      name: "Mililani Post Office",
      type: "USPS Passport Acceptance",
      address: "95-205 Kipapa Dr",
      city: "Mililani",
      state: "HI",
      zip: "96789",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Mililani — about 7 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Mililani area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "pearl-city-honolulu",
      name: "Honolulu Main Post Office",
      type: "USPS Passport Acceptance",
      address: "3350 Manoa Rd",
      city: "Honolulu",
      state: "HI",
      zip: "96822",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Honolulu — about 10 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Honolulu option for Pearl City residents. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Pearl City, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours depending on your eligibility and the processing option you select. Standard government processing through a post office or acceptance clerk in Pearl City can take 2–6 months.",
    },
    {
      q: "Where is the closest passport agency to Pearl City?",
      a: "The Honolulu Passport Agency is located at 300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850 — about 10 miles from Pearl City. Regional agencies require appointments and are primarily reserved for life-or-death emergencies. Fast Passport Center can often provide a faster, more convenient option.",
    },
    {
      q: "Do I need to visit an office to use Fast Passport Center?",
      a: "No — most Pearl City travelers start online and ship their documents via overnight courier. There is no local Fast Passport Center office in Pearl City, but that's the point: you don't need one. Start your order online, follow the checklist, and ship your documents overnight.",
    },
    {
      q: "What documents do I need for an expedited passport?",
      a: "Most applicants need: proof of U.S. citizenship (current or expired U.S. passport, birth certificate, or naturalization certificate), a government-issued photo ID, completed passport forms, and passport photos. We provide a personalized checklist after you start your order.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants typically require an in-person acceptance clerk visit before documents can be submitted for expedited processing. We guide you through exactly what to do and where to go in the Pearl City area.",
    },
    {
      q: "How long does a passport take through the post office in Pearl City?",
      a: "Standard government processing through a Pearl City acceptance facility can take 2–6 months depending on government workload. If your travel date is sooner than that, using an approved passport courier like Fast Passport Center is the safer option.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We are not a government agency. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Pearl City?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions and a checklist. Expedited processing options are available after the acceptance step.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide you through exactly what's needed and help ensure the correct process is followed to avoid delays.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Kamehameha Hwy", address: "1190 Kamehameha Hwy, Pearl City, HI 96782", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Aiea", address: "98-020 Hekaha St, Aiea, HI 96701", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Waipahu", address: "94-300 Farrington Hwy, Waipahu, HI 96797", hours: "Daily 7 AM – 10 PM" },
    { name: "Walmart — Mililani", address: "95-550 Lanikuhana Ave, Mililani, HI 96789", hours: "Call for hours" },
    { name: "The UPS Store — Pearl City", address: "1160 Kamehameha Hwy, Pearl City, HI 96782", hours: "Call for hours" },
    { name: "FedEx Office — Aiea", address: "98-199 Kamehameha Hwy, Aiea, HI 96701", hours: "Call for hours" },
    { name: "Walgreens — Ewa Beach", address: "91-1401 Fort Weaver Rd, Ewa Beach, HI 96706", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Kapolei", address: "590 Farrington Hwy, Kapolei, HI 96707", hours: "Daily 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Pearl City Satellite City Hall",
      address: "1060 Kamehameha Hwy, Pearl City, HI 96782",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Waipahu Satellite City Hall",
      address: "94-300 Farrington Hwy, Waipahu, HI 96797",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Waipahu area option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "Primary airport — about 10 miles from Pearl City",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub for Hawaii with flights to Asia, Australia, North America, and domestic destinations.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: true,
    },
    {
      code: "OGG",
      name: "Kahului Airport (Maui)",
      distance: "Inter-island flights — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Major inter-island airport serving Maui with connections to Honolulu and international destinations.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel. We prioritize your application and submit directly via reserved agency slots.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Pearl City travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide you through every step of the process.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes and delays.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions and checklists.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports and data corrections.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a significantly damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Pearl City travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Pearl City residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Pearl City residents with step-by-step guidance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Pearl City residents with lost or stolen passports." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Pearl City, with parental consent guidance." },
    { name: "Name Change", description: "Passport correction and name change service for Pearl City residents after marriage or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Pearl City travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Pearl City travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Pearl City clients ship from home — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to travel or wait at a passport agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–3", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — drive to downtown Honolulu", fpc: "Most travelers ship documents overnight", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kailua", route: "/cities/kailua" },
    { name: "Kaneohe", route: "/cities/kaneohe" },
    { name: "Hilo", route: "/cities/hilo" },
    { name: "Waipahu", route: "/cities/waipahu" },
    { name: "Ewa Beach", route: "/cities/ewa-beach" },
    { name: "Mililani", route: "/cities/mililani" },
    { name: "Wahiawa", route: "/cities/wahiawa" },
  ],
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your home or office in most cases, ship with our prepaid label from any UPS/FedEx location, or schedule a drop-off. Most clients ship overnight — no visit required.",
  fpcOfficeDistance: "Serving Pearl City and Central Oahu remotely",
  serviceAreaIntro: "Whether you are in Pearl City, Aiea, Waimalu, Waipahu, or anywhere in Central Oahu, Fast Passport Center makes expedited passport processing simple — no agency lines, no guessing.",
  nearbyCitiesIntro: "Anywhere in Central Oahu — start online, ship overnight, and we handle the rest. No need to travel off-island.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Pearl City as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Pearl City travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Pearl City, Aiea, and Waipahu who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Pearl City and Central Oahu travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Oahu",
  islandNickname: "The Gathering Place",
  landmarkCards: [
    {
      name: "Pearl Harbor",
      description: "One of America's most visited historic sites, the USS Arizona Memorial and Pearl Harbor National Memorial are right in Pearl City's backyard. A reminder that international travel — and valid passports — are deeply woven into this community's story.",
      imageUrl: "https://readdy.ai/api/search-image?query=Pearl%20Harbor%20Hawaii%20USS%20Arizona%20Memorial%20with%20American%20flag%2C%20calm%20blue%20harbor%20waters%2C%20lush%20green%20hills%20in%20background%2C%20bright%20sunny%20tropical%20day%2C%20professional%20travel%20photography%2C%20patriotic%20peaceful%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=pearl-city-pearl-harbor&orientation=landscape",
    },
    {
      name: "Aloha Stadium",
      description: "Nearby Aloha Stadium hosts the famous flea market and swap meet — a Central Oahu institution where generations of Pearl City families gather on weekends.",
      imageUrl: "https://readdy.ai/api/search-image?query=Outdoor%20swap%20meet%20and%20flea%20market%20in%20Hawaii%2C%20colorful%20vendor%20stalls%20with%20tropical%20merchandise%2C%20palm%20trees%2C%20bright%20sunny%20day%2C%20vibrant%20community%20atmosphere%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=pearl-city-swap-meet&orientation=landscape",
    },
    {
      name: "Pearlridge Center",
      description: "Central Oahu's largest mall, connected by monorail between two wings. Pearl City residents heading abroad often stop here for last-minute travel items before shipping their passport documents.",
      imageUrl: "https://readdy.ai/api/search-image?query=Modern%20indoor%20shopping%20mall%20in%20Hawaii%20with%20tropical%20plants%20and%20skylights%2C%20clean%20retail%20corridors%2C%20bright%20open%20atmosphere%2C%20professional%20architectural%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=pearl-city-mall&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-flight-takeoff-line", title: "HNL Is 10 Minutes Away", description: "Pearl City's proximity to Honolulu International Airport (HNL) means last-minute passport emergencies are extra stressful. Ship your documents the moment you know — don't wait." },
    { icon: "ri-government-line", title: "Military Family Priority", description: "Many Pearl City residents are active duty or military families. PCS orders, TDY trips, and SOFA travel require valid passports. We handle military urgent requests with priority processing." },
    { icon: "ri-ship-2-line", title: "Asia-Pacific Travel Hub", description: "Pearl City residents frequently travel to the Philippines, Japan, and Korea. Peak travel seasons in spring and summer mean longer government wait times — expedite early." },
    { icon: "ri-truck-line", title: "Ship from Pearl City", description: "UPS and FedEx locations on Kamehameha Hwy give Pearl City residents same-day overnight shipping access. Get documents out before 6pm for next-day receipt." },
    { icon: "ri-building-2-line", title: "Satellite City Hall ID", description: "Pearl City Satellite City Hall at 1060 Kamehameha Hwy handles state IDs and REAL ID — useful if you need updated ID for passport applications." },
    { icon: "ri-calendar-event-line", title: "Cruise Departure Season", description: "Many Pearl City families book Trans-Pacific cruises from Honolulu. Passports for cruises heading to international ports must be valid — book passport renewal months in advance." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
};